import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Header from '../../components/Header';
import { useNavigate } from 'react-router-dom';
import { getEnsayosLaboratorio } from '../../services/laboratorio.services';

const normalizeEnsayosResponse = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  return [];
};

const PENDIENTE_STATES = ['BORRADOR', 'EN_PROCESO', 'EN_ESPERA'];

const formatDate = (value) => {
  if (!value || !dayjs(value).isValid()) {
    return '-';
  }

  return dayjs(value).format('DD/MM/YYYY HH:mm');
};

const getEstadoChipColor = (estado) => {
  if (estado === 'FINALIZADO') {
    return 'success';
  }

  if (estado === 'EN_PROCESO') {
    return 'info';
  }

  if (estado === 'EN_ESPERA') {
    return 'warning';
  }

  return 'default';
};

const getClienteDisplay = (ensayo) =>
  ensayo?.cliente_nombre ||
  ensayo?.razon_social ||
  ensayo?.cliente ||
  ensayo?.id_cliente ||
  '-';

const getComentarioDisplay = (ensayo) =>
  ensayo?.comentario_cierre || ensayo?.comentario || ensayo?.observacion || '-';

const LaboratorioResultadosVista = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pendientes');
  const [search, setSearch] = useState('');
  const [pendientes, setPendientes] = useState([]);
  const [finalizados, setFinalizados] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', severity: 'success' });

  const refreshData = async () => {
    setLoading(true);

    try {
      const [borradorResp, procesoResp, esperaResp, finalizadoResp] = await Promise.all([
        getEnsayosLaboratorio({ estado: 'BORRADOR', page: 1, limit: 300 }),
        getEnsayosLaboratorio({ estado: 'EN_PROCESO', page: 1, limit: 300 }),
        getEnsayosLaboratorio({ estado: 'EN_ESPERA', page: 1, limit: 300 }),
        getEnsayosLaboratorio({ estado: 'FINALIZADO', page: 1, limit: 300 }),
      ]);

      const pendingRows = [
        ...normalizeEnsayosResponse(borradorResp),
        ...normalizeEnsayosResponse(procesoResp),
        ...normalizeEnsayosResponse(esperaResp),
      ];

      setPendientes(pendingRows);
      setFinalizados(normalizeEnsayosResponse(finalizadoResp));
    } catch (error) {
      setSnackbar({
        open: true,
        msg: error?.message || 'No se pudo cargar la vista de resultados de laboratorio',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshData();
  }, []);

  const filteredPendientes = useMemo(() => {
    const token = String(search || '').toLowerCase().trim();

    if (!token) {
      return pendientes;
    }

    return pendientes.filter((ensayo) => {
      const values = [
        ensayo?.id,
        ensayo?.id_produccion,
        ensayo?.serie,
        ensayo?.estado,
        ensayo?.id_cliente,
        getClienteDisplay(ensayo),
      ];

      return values.some((value) => String(value || '').toLowerCase().includes(token));
    });
  }, [pendientes, search]);

  const filteredFinalizados = useMemo(() => {
    const token = String(search || '').toLowerCase().trim();

    if (!token) {
      return finalizados;
    }

    return finalizados.filter((ensayo) => {
      const values = [
        ensayo?.id,
        ensayo?.id_produccion,
        ensayo?.serie,
        ensayo?.estado,
        ensayo?.id_cliente,
        getClienteDisplay(ensayo),
      ];

      return values.some((value) => String(value || '').toLowerCase().includes(token));
    });
  }, [finalizados, search]);

  const rows = tab === 'pendientes' ? filteredPendientes : filteredFinalizados;

  return (
    <Box m="20px">
      <Header
        title="Vista de Resultados de Laboratorio"
        subtitle="Ensayos finalizados y pendientes en una vista separada"
      />

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Buscar"
              placeholder="ID, serie, cliente o estado"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1} flexWrap="wrap">
              <Chip label={`Pendientes: ${pendientes.length}`} color="warning" variant="outlined" />
              <Chip label={`Finalizados: ${finalizados.length}`} color="success" variant="outlined" />
              <Button variant="contained" onClick={refreshData} disabled={loading}>
                {loading ? 'Actualizando...' : 'Actualizar'}
              </Button>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Tabs value={tab} onChange={(_, value) => setTab(value)}>
          <Tab value="pendientes" label="Pendientes" />
          <Tab value="finalizados" label="Finalizados" />
        </Tabs>
      </Paper>

      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <Typography variant="h6" mb={2}>
              {tab === 'pendientes' ? 'Ensayos pendientes' : 'Ensayos finalizados'}
            </Typography>

            {!rows.length ? (
              <Typography variant="body2">No hay ensayos para mostrar en esta seccion.</Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '64vh' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>ID Producción</TableCell>
                      <TableCell>Cliente</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell>Creado</TableCell>
                      <TableCell>Actualizado</TableCell>
                      <TableCell>Comentario</TableCell>
                      <TableCell align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((ensayo) => (
                      <TableRow key={`${ensayo?.id}-${ensayo?.serie || ''}`} hover>
                        <TableCell>{ensayo?.id || '-'}</TableCell>
                        <TableCell>{ensayo?.id_produccion || ensayo?.serie || '-'}</TableCell>
                        <TableCell>{getClienteDisplay(ensayo)}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={ensayo?.estado || '-'}
                            color={getEstadoChipColor(ensayo?.estado)}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{formatDate(ensayo?.created_at)}</TableCell>
                        <TableCell>{formatDate(ensayo?.updated_at || ensayo?.fecha_fin)}</TableCell>
                        <TableCell>{getComentarioDisplay(ensayo)}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Ver / continuar ensayo">
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() =>
                                navigate('/registro/resultadosLaboratorio', {
                                  state: {
                                    ensayoId: ensayo?.id,
                                    id_produccion: ensayo?.id_produccion || ensayo?.serie,
                                    id_cliente: ensayo?.id_cliente,
                                    cliente_nombre: getClienteDisplay(ensayo),
                                  },
                                })
                              }
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LaboratorioResultadosVista;
